import { useEffect, useMemo, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type {
  SalesViewClientDetail,
  SalesViewCohort,
  SalesScoreCatalog,
  SalesOpportunity,
  SalesCloseOutStatus,
  SalesPriceBandsBlock,
  SalesMarketBandsBlock,
  BandKind,
  SalesPriceBandSide,
  SalesPriceBandCell,
  SalesAuctionBands,
  SalesNote,
  PlannedStop,
} from '../../api/types';
import type { AgentHandoff } from '../agent/AgentChatWidget';
import { SalesMiaSummary } from './SalesMiaSummary';
import { SalesKpiTiles } from './SalesKpiTiles';
import { channelLabel } from './salesSnapshot';
import { TIER_COLOR } from './salesViewTokens';
import { SalesOpportunityList } from './SalesOpportunityList';
import { SalesScoreBreakdown } from './SalesScoreBreakdown';
import { SalesGrainTables } from './SalesGrainTables';
import { PercentileRail, ShareBar } from './SalesVisuals';
import { SalesStatusControl } from './SalesStatusControl';
import { statusLabel } from './salesStatusVocab';
import {
  formatPercent,
  formatCount,
  formatDaysAgo,
  formatLiveContact,
  formatVisit,
  formatContactDate,
  formatHealthPercent,
  normalizeDealerGroup,
  getRateTrend,
  isMmrRed,
  productMatrix,
  coxProductMatrix,
  formatMonthLabel,
  paceRead,
  type Trend,
} from './salesViewFormat';
import { METRIC_TIPS } from './salesMetricTips';
import { MIN_RANK_UNITS, sortAuctions, type AuctionSort, type BandPick } from './salesBandSort';
import { readDefaultSection, writeDefaultSection } from './salesDrawerPrefs';
import { pushUsageContext, track } from '../../telemetry/usage';

function TrendMark({ trend }: { trend: Trend }) {
  if (trend.direction === 'new') return <span className="sv-card-change sv-card-change-new">New</span>;
  const glyph = trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '';
  return (
    <span className={`sv-card-change sv-card-change-${trend.direction}`}>
      {glyph} {trend.label}
    </span>
  );
}

function Chip({ label, value, tooltip, sub }: {
  label: string;
  value: React.ReactNode;
  tooltip?: string;
  /** A small second line: the trend, or the detail behind the headline value. */
  sub?: React.ReactNode;
}) {
  return (
    <div className="sv-popout-chip" title={tooltip}>
      <span className="sv-popout-chip-label">{label}</span>
      <span className="sv-popout-chip-value">{value}</span>
      {sub && <span className="sv-popout-chip-sub">{sub}</span>}
    </div>
  );
}

/** Salesforce notes in full: date, channel, type and rep on one line, then subject and body. */
function NotesList({ notes, empty }: { notes: SalesNote[]; empty: string }) {
  if (!notes.length) return <div className="sv-popout-empty">{empty}</div>;
  return (
    <ol className="sv-notes">
      {notes.map((n, i) => {
        const meta = [
          formatContactDate(n.date), channelLabel(n.channel), n.call_task_type || n.type,
          n.agent_name, n.owner_role && n.owner_role !== 'CSE' ? n.owner_role : null,
        ].filter(Boolean);
        return (
          <li key={`${n.date}-${i}`} className="sv-note">
            <div className="sv-note-meta">{meta.join(' · ')}</div>
            {n.subject && <div className="sv-note-subject">{n.subject}</div>}
            {n.description && n.description !== n.subject && <p className="sv-note-body">{n.description}</p>}
          </li>
        );
      })}
    </ol>
  );
}

function Card({ title, note, wide, children }: {
  title: string;
  note?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={`sv-popout-card${wide ? ' sv-popout-card-wide' : ''}`}>
      <h3 className="sv-popout-card-title">{title}</h3>
      {note && <p className="sv-popout-card-note">{note}</p>}
      {children}
    </section>
  );
}

function Kv({ label, value, tone, tip }: {
  label: string;
  value: React.ReactNode;
  tone?: 'red';
  tip?: string;
}) {
  return (
    <div className="sv-popout-kv">
      <span
        className={`sv-popout-kv-label${tip ? ' sv-popout-kv-label-tip' : ''}`}
        title={tip}
      >
        {label}
      </span>
      <span className={`sv-popout-kv-value${tone === 'red' ? ' sv-mmr-red' : ''}`}>{value}</span>
    </div>
  );
}

/** Two-column key/value block. The dense cards ran 12-15 single-column rows each,
 *  which is what turned this panel into a scroll. */
function KvGrid({ children }: { children: React.ReactNode }) {
  return <div className="sv-popout-kv-grid">{children}</div>;
}

/** A STOCK: current level with last month's beside it. Never paced -- pacing a
 *  level would invent units that do not exist. */
function LevelKv({ label, value, prior, percent, tip }: {
  label: string;
  value: number | null | undefined;
  prior: number | null | undefined;
  percent?: boolean;
  tip?: string;
}) {
  const fmt = (v: number | null | undefined) => (percent ? formatPercent(v) : formatCount(v));
  return (
    <Kv
      label={label}
      tip={tip ?? METRIC_TIPS.coxLevel}
      value={
        <>
          {fmt(value)}
          {prior != null && <span className="sv-popout-kv-prior"> vs {fmt(prior)}</span>}
        </>
      }
    />
  );
}

/** A FLOW mid-month: the projected full month against last month, never the raw
 *  month-to-date. Comparing MTD to a full prior month makes every dealer look like
 *  they are collapsing on the 10th. */
function PaceKv({ label, mtd, prior, pace, reliable, tip }: {
  label: string;
  mtd: number | null | undefined;
  prior: number | null | undefined;
  pace: number | null | undefined;
  reliable: boolean;
  tip?: string;
}) {
  const read = reliable ? paceRead(pace, prior) : null;
  return (
    <div className="sv-popout-kv">
      <span
        className="sv-popout-kv-label sv-popout-kv-label-tip"
        title={tip ?? (reliable ? METRIC_TIPS.coxPace : METRIC_TIPS.coxPaceEarly)}
      >
        {label}
      </span>
      <span className="sv-popout-kv-value">
        {formatCount(mtd)}
        {prior != null && <span className="sv-popout-kv-prior"> vs {formatCount(prior)}</span>}
        {read && <TrendMark trend={{ direction: read.direction, label: read.label }} />}
      </span>
    </div>
  );
}

const SECTIONS = [
  { key: 'snapshot', label: 'Snapshot' },
  { key: 'why', label: 'Why' },
  { key: 'performance', label: 'Performance' },
  { key: 'profile', label: 'Profile' },
  { key: 'activity', label: 'Activity' },
] as const;

type SectionKey = (typeof SECTIONS)[number]['key'];

/** Percentile tone, flipped for metrics where lower is better. */
function toneFor(percentile: number, higherIsBetter: boolean): 'good' | 'warn' | 'bad' {
  const p = higherIsBetter ? percentile : 1 - percentile;
  return p >= 0.6 ? 'good' : p >= 0.3 ? 'warn' : 'bad';
}

/** Cohort values are mixed units — ratios, dollars, days and plain counts. */
function formatCohortValue(value: number | null | undefined, unit: string): string {
  if (value == null) return '—';
  if (unit === 'currency') return `$${Math.round(value).toLocaleString()}`;
  if (unit === 'days') return `${value.toFixed(1)}d`;
  if (unit === 'count') return value.toFixed(2);
  return formatPercent(value);
}

/** Dollars, signed. Ancillary revenue is net of credits, so negatives are real
 *  and must not render as "$-1,204" the way a bare template literal would. */
function money(value: number | null | undefined): string {
  if (value == null) return '—';
  const n = Math.round(value);
  return `${n < 0 ? '−$' : '$'}${Math.abs(n).toLocaleString()}`;
}

/**
 * Buy/sell volume by price band, and by auction within each band (S1.15) --
 * the "All Sales by Price Buckets" workbook applied to one rooftop.
 *
 * Auctions are collapsed behind the client total by default. The band summary is
 * the answer to "what kind of money does this dealer move"; the auction split is
 * a follow-up question, and putting a 10-row grid in front of it buried the part
 * a rep actually reads.
 *
 * A band with no units is OMITTED, not drawn as zero. The median CSE seller moves
 * 4 cars a quarter and fills one or two of the five bands, so rendering empties
 * would make four fifths of every chart whitespace.
 */
function PriceBandsSection({ clientId }: { clientId: string }) {
  const [windowMonths, setWindowMonths] = useState<3 | 12>(3);
  const [scope, setScope] = useState<'client' | 'market'>('client');
  const [kind, setKind] = useState<BandKind>('price');
  const [data, setData] = useState<SalesPriceBandsBlock | null>(null);
  const [market, setMarket] = useState<SalesMarketBandsBlock | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (scope === 'client') {
          setData(await siftApiClient.getSalesViewClientPriceBands(
            clientId, windowMonths, undefined, controller.signal, kind));
        } else {
          setMarket(await siftApiClient.getSalesViewMarketBands(
            windowMonths, undefined, controller.signal, kind));
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Price bands load failed:', err);
          setError('Could not load price bands.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [clientId, windowMonths, scope, kind]);

  // Which lanes this client already trades, so the market view can say "you are
  // here" rather than leaving the rep to cross-reference two tables by eye.
  const ownAuctions = useMemo(() => {
    const names = new Set<string>();
    for (const side of [data?.sell, data?.buy]) {
      for (const a of side?.auctions ?? []) names.add(a.auction);
    }
    return names;
  }, [data]);

  const shown = scope === 'client' ? data : market;
  const hasData = !!shown?.has_data;

  return (
    <>
      <div className="sv-band-controls">
        <div className="sv-band-window" role="group" aria-label="Rolling window">
          {([3, 12] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`sv-band-window-btn${windowMonths === m ? ' is-active' : ''}`}
              aria-pressed={windowMonths === m}
              onClick={() => setWindowMonths(m)}
            >
              {m} mo
            </button>
          ))}
        </div>
        <div className="sv-band-window" role="group" aria-label="Scope">
          {([['client', 'This client'], ['market', 'All auctions']] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`sv-band-window-btn${scope === key ? ' is-active' : ''}`}
              aria-pressed={scope === key}
              onClick={() => setScope(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="sv-band-window" role="group" aria-label="Band by">
          {([['price', 'Price'], ['mileage', 'Mileage']] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`sv-band-window-btn${kind === key ? ' is-active' : ''}`}
              aria-pressed={kind === key}
              onClick={() => setKind(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="sv-popout-empty">Loading price bands…</div>}
      {error && <div className="sv-popout-empty">{error}</div>}
      {!loading && !error && !hasData && (
        <div className="sv-popout-empty">
          {scope === 'client'
            ? `No wholesale activity in the last ${windowMonths} months.`
            : 'No auction activity available yet.'}
        </div>
      )}
      {!loading && !error && hasData && (
        <div className="sv-band-sides">
          {shown?.sell && (
            <PriceBandSide
              key={`sell-${kind}`}
              side={shown.sell}
              label={scope === 'client' ? 'Sold' : 'Sold — all auctions'}
              ownAuctions={scope === 'market' ? ownAuctions : undefined}
              kind={kind}
            />
          )}
          {shown?.buy && (
            <PriceBandSide
              key={`buy-${kind}`}
              side={shown.buy}
              label={scope === 'client' ? 'Bought' : 'Bought — all auctions'}
              ownAuctions={scope === 'market' ? ownAuctions : undefined}
              kind={kind}
            />
          )}
        </div>
      )}
    </>
  );
}

/** Auctions shown before the rep asks for the rest. Median is 4 and p90 is 21, so
 *  this covers most dealers outright while keeping a 176-auction tail one click
 *  away rather than absent. */
const AUCTIONS_SHOWN = 10;

/** "vs 14 other sellers here" — the comparison spelled out, because a bare ▲ next
 *  to a number invites the reader to invent what it is measured against. */
function marketTip(b: SalesPriceBandCell): string | undefined {
  if (b.market_units == null || b.market_avg_price == null) return undefined;
  const mine = b.avg_price != null ? `$${Math.round(b.avg_price).toLocaleString()}` : '—';
  const theirs = `$${Math.round(b.market_avg_price).toLocaleString()}`;
  const who = b.market_clients ? ` across ${b.market_clients} other seller${b.market_clients === 1 ? '' : 's'}` : '';
  const mmr = b.pct_mmr != null && b.market_pct_mmr != null
    ? ` · MMR retention ${formatPercent(b.pct_mmr)} vs ${formatPercent(b.market_pct_mmr)}`
    : '';
  return `${b.band}: this client ${mine} on ${formatCount(b.units)} units, `
    + `the rest of this lane ${theirs} on ${formatCount(b.market_units)}${who}${mmr}`;
}

/** The full measure set for a set of bands. Used for the side summary AND for each
 *  auction, so a lane is never a thinner read than the total it rolls into. */
function BandTable({ bands, side }: { bands: SalesPriceBandCell[]; side: string }) {
  const hasMarket = bands.some((b) => b.market_avg_price != null);
  const retentionTip = side === 'BUY' ? METRIC_TIPS.mmrRetentionBuy : METRIC_TIPS.mmrRetentionSell;
  return (
    <table className="sv-band-table">
      <thead>
        <tr>
          <th>Band</th>
          <th>Units</th>
          <th>Share</th>
          <th>Avg price</th>
          <th title={retentionTip}>MMR retention</th>
          <th>Avg CR</th>
          {hasMarket && <th title="The rest of this lane, excluding this client">vs lane</th>}
        </tr>
      </thead>
      <tbody>
        {bands.map((b) => (
          <tr key={b.band_ord}>
            <td>{b.band}</td>
            <td>{formatCount(b.units)}</td>
            <td>{b.share != null ? formatPercent(b.share) : '—'}</td>
            <td>{b.avg_price != null ? `$${Math.round(b.avg_price).toLocaleString()}` : '—'}</td>
            {/* Blank, not zero, when MMR coverage is too thin to stand behind. */}
            <td title={b.pct_mmr == null && b.mmr_coverage != null
              ? `Only ${formatPercent(b.mmr_coverage)} of these cars carry an MMR — too few to quote a ratio`
              : undefined}>
              {b.pct_mmr != null ? formatPercent(b.pct_mmr) : '—'}
            </td>
            <td title={b.pct_without_cr != null
              ? `${formatPercent(b.pct_without_cr)} sold without a CR`
              : undefined}>
              {b.avg_cr_grade != null ? b.avg_cr_grade.toFixed(1) : '—'}
            </td>
            {hasMarket && (
              <td title={marketTip(b)}>
                {b.market_avg_price != null ? (
                  <>
                    ${Math.round(b.market_avg_price).toLocaleString()}
                    {b.avg_price != null && (
                      <i className={`sv-band-vs sv-band-vs-${
                        b.avg_price >= b.market_avg_price ? 'up' : 'down'}`}>
                        {b.avg_price >= b.market_avg_price ? '▲' : '▼'}
                      </i>
                    )}
                  </>
                ) : '—'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** One auction, collapsed to a headline until asked. Expanding gives the SAME
 *  table as the side summary -- the previous inline chips could only carry units
 *  and wrapped into a wall on a narrow drawer. */
function AuctionRow({ auction, isOwn, side, band, units, retention }: {
  auction: SalesAuctionBands;
  isOwn: boolean;
  side: string;
  band: BandPick;
  units: number;
  retention: number | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="sv-band-auction">
      <button
        type="button"
        className="sv-band-auction-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sv-band-auction-caret">{open ? '▾' : '▸'}</span>
        <span className="sv-band-auction-name">
          {isOwn && <span className="sv-band-here" title="This client already trades here">●</span>}
          {auction.auction}
        </span>
        <span className="sv-band-auction-meta">
          {formatCount(units)} units{band !== 'all' && ' in band'}
          {auction.clients != null && ` · ${formatCount(auction.clients)} dealers`}
          {retention != null && ` · ${formatPercent(retention)} MMR`}
        </span>
      </button>
      {open && (
        <div className="sv-band-auction-body">
          <BandTable bands={auction.bands} side={side} />
        </div>
      )}
    </li>
  );
}

function PriceBandSide({ side, label, ownAuctions, kind = 'price' }: {
  side: SalesPriceBandSide;
  label: string;
  /** Market view: lanes this client already trades, marked so a rep can tell
   *  "where I am" from "where I am not" without reading two tables. */
  ownAuctions?: Set<string>;
  kind?: BandKind;
}) {
  const [showAuctions, setShowAuctions] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState<AuctionSort>('volume');
  const [band, setBand] = useState<BandPick>('all');
  const ordered = useMemo(() => sortAuctions(side.auctions, sort, band), [side.auctions, sort, band]);
  // Every auction is present; a dealer can run 176 of them. Lead with the ones
  // that carry their volume and put the tail one click away.
  const visible = showAll ? ordered : ordered.slice(0, AUCTIONS_SHOWN);

  return (
    <div className="sv-band-side">
      <div className="sv-band-side-head">
        <span className="sv-band-side-label">{label}</span>
        <span className="sv-band-side-total">{formatCount(side.units)} units</span>
      </div>

      <BandTable bands={side.bands} side={side.side} />

      {side.auctions.length > 0 && (
        <button
          type="button"
          className="sv-band-toggle"
          aria-expanded={showAuctions}
          onClick={() => setShowAuctions((v) => !v)}
        >
          {showAuctions ? '▾' : '▸'} {side.auctions.length} auction
          {side.auctions.length === 1 ? '' : 's'}
        </button>
      )}

      {showAuctions && (
        <div className="sv-band-sort">
          <label>
            Sort
            <select
              aria-label="Sort auctions"
              value={sort}
              onChange={(e) => { setSort(e.target.value as AuctionSort); setShowAll(false); }}
            >
              <option value="volume">Busiest first</option>
              <option value="retention_asc">MMR retention, low to high</option>
              <option value="retention_desc">MMR retention, high to low</option>
            </select>
          </label>
          <label>
            Band
            <select
              aria-label={kind === 'mileage' ? 'Mileage band' : 'Price band'}
              value={String(band)}
              onChange={(e) => {
                setBand(e.target.value === 'all' ? 'all' : Number(e.target.value));
                setShowAll(false);
              }}
            >
              <option value="all">All bands</option>
              {side.bands.map((b) => (
                <option key={b.band_ord} value={b.band_ord}>{b.band}</option>
              ))}
            </select>
          </label>
          {sort !== 'volume' && (
            <span
              className="sv-band-sort-note"
              title={`Lanes with fewer than ${MIN_RANK_UNITS} units in the chosen band, or too few MMRs to quote retention, are listed after the ranked ones — a handful of cars is not a price position.`}
            >
              Under {MIN_RANK_UNITS} units listed last
            </span>
          )}
        </div>
      )}

      {showAuctions && (
        <ul className="sv-band-auctions">
          {visible.map((r) => (
            <AuctionRow
              key={r.auction.auction}
              auction={r.auction}
              isOwn={!!ownAuctions?.has(r.auction.auction)}
              side={side.side}
              band={band}
              units={r.units}
              retention={r.retention}
            />
          ))}
        </ul>
      )}

      {showAuctions && ordered.length > AUCTIONS_SHOWN && (
        <button
          type="button"
          className="sv-band-toggle"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? `Show top ${AUCTIONS_SHOWN}`
            : `Show all ${ordered.length} auctions`}
        </button>
      )}
    </div>
  );
}

/** CSE-12: territory, or same brand (independents: other independents) within a radius. The
 *  backend widens a thin same-brand cohort to reach 5 peers and says so in the definition. */
/** CSE-14: "23 days · Warm", either half optional; blank when the market had no row. */
function marketCell(days?: number | null, demand?: string | null): string {
  const parts = [days != null ? `${days} days` : null, demand || null].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

const COHORT_CHOICES = [
  { value: 'territory', label: 'CSE territory' },
  { value: 'brand:10', label: 'Same brand within 10 mi' },
  { value: 'brand:25', label: 'Same brand within 25 mi' },
  { value: 'brand:50', label: 'Same brand within 50 mi' },
  { value: 'brand:75', label: 'Same brand within 75 mi' },
] as const;

function CohortSection({ clientId }: { clientId: string }) {
  const [cohort, setCohort] = useState<SalesViewCohort | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<string>('territory');

  /* Loads with the popout rather than behind a click. It was gated because the
   * endpoint is "heavier" than the detail one -- true in ratio (65ms vs 0.1ms,
   * measured over 40 clients against the full 40,570-client book) and irrelevant
   * in absolute terms. 65ms is imperceptible, and a button that says "load the
   * rest of the page" costs more than the request does. */
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setCohort(null);
    (async () => {
      const [basis, radius] = choice.split(':');
      try {
        setCohort(await siftApiClient.getSalesViewClientCohort(
          clientId, undefined, controller.signal,
          basis === 'brand' ? { basis: 'brand', radiusMi: Number(radius) } : undefined,
        ));
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Cohort load failed:', err);
          setError('Could not load peer cohort.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [clientId, choice]);

  const picker = (
    <label className="sv-cohort-basis">
      Compare with{' '}
      <select
        aria-label="Compare with"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
      >
        {COHORT_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
    </label>
  );

  if (loading) return <>{picker}<div className="sv-popout-empty">Loading cohort…</div></>;
  if (error) return <>{picker}<div className="sv-popout-empty">{error}</div></>;
  if (!cohort) return picker;

  return (
    <>
      {picker}
      <p className="sv-popout-card-note">
        How this rooftop compares with {cohort.cohort_size.toLocaleString()} peers:{' '}
        {cohort.cohort_definition || 'its peer set'}, over the rolling 3-month period.
        Use it to tell whether a weak number is this dealer or the whole market.
      </p>
      {cohort.deltas.length === 0 ? (
        <div className="sv-popout-empty">No cohort comparison available.</div>
      ) : (
        <table className="sv-popout-table sv-cohort-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="sv-num">This client</th>
              <th title={METRIC_TIPS.peerDefinition}>vs peers</th>
            </tr>
          </thead>
          <tbody>
            {cohort.deltas.map((d) => (
              <tr key={d.metric}>
                <td className="sv-popout-metric-label">{d.label || d.metric}</td>
                <td className="sv-num">
                  <div className="sv-cohort-value">{formatCohortValue(d.client_value, d.unit)}</div>
                  {/* Peer median under the value rather than its own column: four
                      columns plus a rail could not fit the panel at typical widths. */}
                  <div className="sv-cohort-peer">
                    peer {formatCohortValue(d.cohort_median, d.unit)}
                  </div>
                </td>
                <td className="sv-rail-cell">
                  {/* On a lower-is-better metric a low percentile is GOOD, so the
                      tone is flipped rather than the number. */}
                  <PercentileRail
                    percentile={d.percentile}
                    tone={d.percentile == null ? undefined : toneFor(d.percentile, d.higher_is_better)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {cohort.price_band && (
        <Kv
          label="Price band"
          value={`${cohort.price_band}${
            cohort.price_band_position != null
              ? ` · ${Math.round(cohort.price_band_position * 100)}th pct`
              : ''
          }${
            cohort.price_band_cohort_size > 0
              ? ` of ${cohort.price_band_cohort_size.toLocaleString()}`
              : ''
          }`}
        />
      )}
    </>
  );
}

interface SalesClientDrawerProps {
  clientId: string;
  impersonatedRole: string | null;
  /** Fetched once by the dashboard so every popout shares one copy. */
  scoreCatalog: SalesScoreCatalog | null;
  /** The opportunity vocabulary from `/filter-options`, for the measured-over spans. */
  opportunityCatalog?: SalesOpportunity[] | null;
  onClose: () => void;
  onStatusChange: (clientId: string, status: SalesCloseOutStatus, updatedAt: string | null) => void;
  /** Phones and tablets: the popup covers the screen, so closing it reads as going back. */
  fullScreen?: boolean;
  /** True when this client's board card is visible beside the popout. The popout then leaves
   *  health, volume and contact recency to the card instead of repeating them. */
  cardOnScreen?: boolean;
  /** This client's stop in the loaded week: null when the plan is loaded and it is not on it,
   *  undefined when no plan has been loaded. */
  plannedStop?: PlannedStop | null;
  /** Set when Sales Mia is enabled: shows the Mia summary and hands its thread to the widget. */
  onContinueInMia?: (handoff: AgentHandoff) => void;
}

/**
 * Client detail popout (§6.5). Four tabs — Why / Performance / Profile / Activity
 * — with the header, headline strip and close-out outside the scroll container.
 *
 * Takes over the MAP slot rather than sliding in over the app, matching
 * `VinDetailsPopout` in Client View: the carousel stays put and the splitter the
 * user already dragged still governs the width, so the two tabs behave the same
 * way and nothing is hidden behind an overlay.
 *
 * "Why this client surfaced" leads deliberately: the score is the reason the rep
 * is looking at this rooftop, and burying the explanation under a metric grid is
 * how a scored list loses trust. `target_missing` is a DATA-QUALITY flag, not a
 * risk signal, so it is called out in the Core Car card and kept out of the
 * reason list entirely (§4.4.1).
 */
export function SalesClientDrawer({
  clientId,
  impersonatedRole,
  scoreCatalog,
  opportunityCatalog,
  onClose,
  onStatusChange,
  fullScreen = false,
  cardOnScreen = false,
  plannedStop,
  onContinueInMia,
}: SalesClientDrawerProps) {
  useEffect(() => {
    track('sales_view', 'client_popout', 'open', { seller_uid: clientId });
    return pushUsageContext('client_popout', { seller_uid: clientId });
  }, [clientId]);
  const [detail, setDetail] = useState<SalesViewClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [section, setSection] = useState<SectionKey>(() => readDefaultSection());
  const [firstTab, setFirstTab] = useState<SectionKey>(() => readDefaultSection());

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSection(readDefaultSection());
    (async () => {
      try {
        setDetail(await siftApiClient.getSalesViewClientDetail(
          clientId, impersonatedRole || undefined, controller.signal,
        ));
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Client detail load failed:', err);
          setError('Could not load this client.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [clientId, impersonatedRole]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tier = detail ? (TIER_COLOR[detail.tier] ?? TIER_COLOR.LOW) : TIER_COLOR.LOW;
  const place = detail
    ? [detail.territory_city, detail.territory_state, detail.territory_zip].filter(Boolean).join(', ')
    : '';
  const m = detail?.metrics;
  const e = detail?.engagement;
  const latestNote = detail?.cse_notes?.[0] ?? detail?.recent_notes?.[0] ?? null;
  const otherTeamNotes = (detail?.recent_notes ?? []).filter((n) => n.owner_role !== 'CSE');
  // The week's own words when the client is on it; the planner's reading of it otherwise.
  const view = detail?.plan_view;
  const reasonForVisit = plannedStop?.reason_for_visit || view?.reason_for_visit || null;
  const points = plannedStop?.talking_points?.length ? plannedStop.talking_points : (view?.talking_points ?? []);
  const mode = (s?: string | null) => (s === 'VISIT' ? 'visit' : 'call');
  const planNote = plannedStop
    ? `On this week's plan: ${mode(plannedStop.contact_mode)}${plannedStop.trip_no ? `, trip ${plannedStop.trip_no}` : ''} · ${plannedStop.visit_type}`
    : view?.contact_mode
      ? `${plannedStop === null ? 'Not on this week\'s plan. ' : ''}Plan My Week reads it as a ${mode(view.contact_mode)}: ${view.visit_type}`
      : undefined;
  const pipelineSub = [
    detail?.pipeline.offtrack_opps ? `${formatCount(detail.pipeline.offtrack_opps)} off track` : null,
    detail?.pipeline.next_close_date ? `closes ${formatContactDate(detail.pipeline.next_close_date)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <section
      className={`sv-popout-panel${fullScreen ? ' sv-popout-panel--full' : ''}`}
      role="dialog"
      aria-label="Client detail"
      aria-modal={fullScreen || undefined}
    >
      <div className="sv-popout-header">
        {fullScreen && (
          <button type="button" className="sv-popout-back" onClick={onClose}>
            ‹ Clients
          </button>
        )}
        <div className="sv-popout-title">
          <h2>{detail?.client_name || clientId}</h2>
          <span className="sv-popout-id">{clientId}</span>
          {detail && (
            <span
              className="sv-popout-tier"
              style={{ background: tier.bg, color: tier.fg }}
              title={detail.score_version ? `Score ${detail.score_version}` : undefined}
            >
              {detail.tier}
            </span>
          )}
          {place && <span className="sv-popout-place">{place}</span>}
          {detail?.salesforce_id && (
            <a
              className="sv-popout-salesforce-link"
              href={`https://manheim.lightning.force.com/lightning/r/Account/${detail.salesforce_id}/view`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open client profile in Salesforce"
            >
              Salesforce ↗
            </a>
          )}
        </div>
        {!fullScreen && (
          <button type="button" className="sv-popout-close" onClick={onClose} aria-label="Close">✕</button>
        )}
      </div>

      {/* Strip and tabs sit OUTSIDE the scroll container, so they hold across tabs. The strip
          carries what the board card does NOT: health, volume and recency are on the card. */}
      {detail && e && !loading && (
        <>
          <div className="sv-popout-strip">
            <Chip
              label="CSE contact · 90 days"
              value={e.cse_live_contact_count_90d != null
                ? `${formatCount(e.cse_live_contact_count_90d)} conversations` : '—'}
              sub={`${formatCount(e.cse_in_person_contact_count_90d)} visits · ${formatCount(e.cse_contact_count_90d)} attempts`}
              tooltip="CSE tasks in the last 90 days: how many reached someone live, how many were in person, and every attempt. Conversations are a floor: some tasks record no channel."
            />
            <Chip
              label="Pipeline"
              value={detail.pipeline.has_data ? `${formatCount(detail.pipeline.opp_open)} open` : 'None'}
              sub={pipelineSub || undefined}
              tooltip="Open Salesforce opportunities, how many are off track, and the next close date."
            />
            <Chip
              label="Ancillary · 13 weeks"
              value={detail.ancillary.has_data ? money(detail.ancillary.revenue.total_13wk ?? 0) : '—'}
              sub={detail.ancillary.has_data ? `${detail.ancillary.enrolled?.length ?? 0} products held` : undefined}
              tooltip="Ancillary revenue billed in the last 13 weeks, net of credits, and the products they hold."
            />
          </div>

          <nav className="sv-popout-tabs" role="tablist" aria-label="Client detail sections">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={section === s.key}
                className={`sv-popout-tab${section === s.key ? ' sv-popout-tab-active' : ''}`}
                onClick={() => setSection(s.key)}
              >
                {s.label}
              </button>
            ))}
            {section === firstTab ? (
              <span className="sv-popout-tab-first" title="Clients open on this tab">★ opens first</span>
            ) : (
              <button
                type="button"
                className="sv-popout-tab-first"
                title="Open every client on this tab (this browser only)"
                onClick={() => { writeDefaultSection(section); setFirstTab(section); }}
              >
                ☆ Open clients here
              </button>
            )}
          </nav>
        </>
      )}

      <div className="sv-popout-body">
        {loading && <div className="sv-popout-loading">Loading…</div>}
        {error && <div className="sv-error">{error}</div>}

        {detail && !loading && (
          <>
            {section === 'snapshot' && (
              <div className="sv-popout-grid">
                {onContinueInMia && detail.client_id === clientId && (
                  // Only once THIS client's detail is in: stepping to the next client briefly
                  // renders the previous one's detail, and mounting here then fired the summary
                  // twice (the first aborted, but still generated server-side).
                  <div className="sv-popout-card-wide">
                    <SalesMiaSummary key={clientId} clientId={clientId}
                      impersonateRole={impersonatedRole || undefined} onContinue={onContinueInMia} />
                  </div>
                )}

                {!cardOnScreen && m && e && (
                  <Card title="At a glance" wide>
                    <p className="sv-glance-line">
                      Health {formatHealthPercent(detail.health_score)}
                      {detail.territory_rank ? ` (#${detail.territory_rank} in territory)` : ''}
                      {' \u00b7 '}{formatDaysAgo(e.days_since_last_cse_contact)} since CSE contact
                      {e.days_since_last_cse_contact != null
                        ? ` \u00b7 ${formatLiveContact(e.days_since_last_cse_live_contact)} \u00b7 ${formatVisit(e.days_since_last_cse_in_person_contact)}`
                        : ''}
                    </p>
                    <SalesKpiTiles m={m} />
                  </Card>
                )}

                <Card title="Reason to contact" note={planNote} wide>
                  {reasonForVisit && <p className="sv-focus-reason">{reasonForVisit}</p>}
                  {points.length ? (
                    <ul className="sv-talking-points" aria-label="Talking points">
                      {points.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                  ) : null}
                  {!reasonForVisit && !points.length && (
                    <div className="sv-popout-empty">Nothing beyond a routine touch.</div>
                  )}
                  <p className="sv-popout-card-note">Every signal behind the score is on the Why tab.</p>
                </Card>

                <Card title="Latest CSE note" wide>
                  {latestNote
                    ? <NotesList notes={[latestNote]} empty="" />
                    : <div className="sv-popout-empty">No CSE notes in Salesforce.</div>}
                </Card>
              </div>
            )}

            {section === 'why' && (
              <div
                className={`sv-popout-grid${
                  (detail.opportunities ?? []).length > 0 ? ' sv-popout-grid-why' : ''
                }`}
              >
                <Card
                  title="Why this client surfaced"
                  wide={(detail.opportunities ?? []).length === 0}
                >
                  <SalesScoreBreakdown
                    healthScore={detail.health_score}
                    reasons={detail.reasons}
                    scoreVersion={detail.score_version}
                    catalog={scoreCatalog}
                  />
                </Card>
                {(detail.opportunities ?? []).length > 0 && (
                  <Card title="What is going right">
                    <SalesOpportunityList
                      opportunities={detail.opportunities ?? []}
                      catalog={opportunityCatalog}
                    />
                  </Card>
                )}
              </div>
            )}

            {section === 'performance' && (
              <div className="sv-popout-grid">
                <Card title="Performance by grain" wide>
                  <SalesGrainTables windows={detail.windows} />
                </Card>

                <Card
                  title="Channel mix"
                  note="Rolling 3 months (PERIOD) against the prior 3. Shares are summed numerators over summed denominators, never an average of rates."
                >
                  <ShareBar
                    segments={[
                      { key: 'live', label: 'Live', value: detail.channel_mix.sell_live_share, color: TIER_COLOR.MEDIUM.pin },
                      { key: 'timed', label: 'Timed', value: detail.channel_mix.sell_timed_share, color: TIER_COLOR.HIGH.pin },
                    ]}
                  />
                  <KvGrid>
                    <Kv label="Live conversion" value={formatPercent(detail.channel_mix.sell_live_conversion)} tip={METRIC_TIPS.liveConversion} />
                    <Kv label="Timed conversion" value={formatPercent(detail.channel_mix.sell_timed_conversion)} tip={METRIC_TIPS.timedConversion} />
                    <Kv label="Live share (buy)" value={formatPercent(detail.channel_mix.buy_live_share)} />
                    <Kv
                      label="Shift vs prior"
                      value={<TrendMark trend={getRateTrend(detail.channel_mix.shift_vs_prior, 0)} />}
                    />
                  </KvGrid>
                </Card>

                <Card title="Cohort &amp; market">
                  <CohortSection clientId={clientId} />
                </Card>

                <Card
                  title="Price &amp; mileage bands by auction"
                  note="Net of back-outs, anchored on the sale-close day. Mirrors the “All Sales by Price Buckets” report at rooftop level, so it will not tie to the listing-grain sell numbers above. Mileage bands cut the same sales by odometer; Avg price and MMR retention still describe the sale price."
                  wide
                >
                  <PriceBandsSection clientId={clientId} />
                </Card>
              </div>
            )}

            {section === 'profile' && (
              <div className="sv-popout-grid">
                <Card title="Core car book (Salesforce)">
                  {detail.target_missing && (
                    <div className="sv-popout-flag">
                      No SFDC target set. Market-share signals are suppressed rather than scored
                      against a missing target.
                    </div>
                  )}
                  <KvGrid>
                    <Kv label="Dealer group" value={normalizeDealerGroup(detail.core_car.dealer_group_name) || '—'} />
                    <Kv label="Owner" value={detail.core_car.owner_name || '—'} tip="The Salesforce account owner. This is the field the Sales book is scoped on." />
                    <Kv label="Sold YTD" value={formatCount(detail.core_car.total_sold_ytd)} tip={METRIC_TIPS.coreCarRollup} />
                    <Kv label="Sold LY" value={formatCount(detail.core_car.total_sold_ly)} tip={METRIC_TIPS.coreCarRollup} />
                    <Kv label="Bought YTD" value={formatCount(detail.core_car.total_bought_ytd)} tip={METRIC_TIPS.coreCarRollup} />
                    <Kv label="Bought LY" value={formatCount(detail.core_car.total_bought_ly)} tip={METRIC_TIPS.coreCarRollup} />
                    {detail.core_car.current_month_seller_count != null && (
                      <Kv label="Sold MTD" value={formatCount(detail.core_car.current_month_seller_count)} tip={METRIC_TIPS.coreCarCurrentMonth} />
                    )}
                    {detail.core_car.current_month_buyer_count != null && (
                      <Kv label="Bought MTD" value={formatCount(detail.core_car.current_month_buyer_count)} tip={METRIC_TIPS.coreCarCurrentMonth} />
                    )}
                    <Kv label="Monthly wholesale" value={formatCount(detail.core_car.monthly_wholesale_volume)} tip={METRIC_TIPS.coreCarSelfReported} />
                    <Kv label="Monthly retail" value={formatCount(detail.core_car.monthly_retail_volume)} tip={METRIC_TIPS.coreCarSelfReported} />
                  </KvGrid>
                </Card>

                <Card
                  title="Ancillary products"
                  note="Salesforce enrolment plus what those products actually billed. Enrolment is what they are signed up for; the revenue block below is the money, and a client can be enrolled and billing nothing."
                >
                  {!detail.ancillary.has_data ? (
                    <p className="sv-popout-card-note">
                      No Salesforce ancillary record. Unknown, not “owns nothing”.
                    </p>
                  ) : (
                    <>
                      <div className="sv-anc-chips">
                        {productMatrix(detail.ancillary).map((p) => (
                          <span
                            key={p.label}
                            className={`sv-anc-chip${p.owned ? ' sv-anc-chip-on' : ''}`}
                            title={p.owned ? 'Enrolled' : 'Not on record — whitespace'}
                          >
                            {p.label}
                          </span>
                        ))}
                      </div>

                      {detail.ancillary.products?.length ? (
                        <table className="sv-popout-table sv-anc-detail">
                          <thead>
                            <tr><th>Product</th><th>Status</th><th>Since</th><th>Rate</th><th>Last billed</th></tr>
                          </thead>
                          <tbody>
                            {detail.ancillary.products.map((p) => (
                              <tr key={`${p.product}-${p.status}`}>
                                <td>{p.product}</td>
                                <td
                                  className={p.status === 'stopped' ? 'sv-anc-stopped' : undefined}
                                  title={p.status === 'stopped'
                                    ? 'Billed before, nothing in 90 days. Salesforce records no cancellation, so this is read from billing.'
                                    : undefined}
                                >
                                  {p.status === 'stopped' ? 'Stopped' : 'Active'}
                                </td>
                                <td>{p.since ? formatContactDate(p.since) : '—'}</td>
                                <td>
                                  {p.plan ?? ([
                                    p.amount != null ? `$${Math.round(p.amount).toLocaleString()}` : null,
                                    p.term_days ? `${p.term_days}-day` : null,
                                  ].filter(Boolean).join(' · ') || '—')}
                                </td>
                                <td>{p.last_billed
                                  ? `${formatContactDate(p.last_billed)} · ${formatDaysAgo(p.days_since_billed)}`
                                  : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : null}

                      <KvGrid>
                        {!detail.ancillary.products?.length && detail.ancillary.ds_360_21_rate && (
                          <Kv label="360/21 quote" value={detail.ancillary.ds_360_21_rate} />
                        )}
                        {!detail.ancillary.products?.length && detail.ancillary.ds_360_30_rate && (
                          <Kv label="360/30 quote" value={detail.ancillary.ds_360_30_rate} />
                        )}
                        {detail.ancillary.cd_subscription_type && (
                          <Kv label="CD plan" value={detail.ancillary.cd_subscription_type} />
                        )}
                        <Kv label="RL shipments 3mo" value={formatCount(detail.ancillary.ready_txn_3mo)} tip={METRIC_TIPS.ancillaryReadyShipments} />
                        <Kv label="RL shipments 12mo" value={formatCount(detail.ancillary.ready_txn_12mo)} tip={METRIC_TIPS.ancillaryReadyShipments} />
                        <Kv label="RL last shipment" value={formatContactDate(detail.ancillary.ready_last_txn_date)} />
                      </KvGrid>
                      <p className="sv-popout-card-note">
                        Solid chips are enrolled; faded ones are whitespace, not a verified “no”.
                        RL registration sits at ~99.5% of accounts, so shipments are the usage read.
                      </p>

                      <div className="sv-popout-subhead">Revenue</div>
                      {!detail.ancillary.revenue.has_data ? (
                        <p className="sv-popout-card-note">
                          No ancillary revenue in the last 13 weeks. A known zero, not missing data —
                          any enrolled chip above is whitespace this client is not spending against.
                        </p>
                      ) : (
                        <>
                          <KvGrid>
                            <Kv
                              label="Last week"
                              tip={METRIC_TIPS.ancillaryRevenueWindows}
                              tone={(detail.ancillary.revenue.lw_vs_4wk_pct ?? 0) <= -0.5 ? 'red' : undefined}
                              value={money(detail.ancillary.revenue.lw)}
                            />
                            <Kv
                              label="4-wk avg / wk"
                              tip={METRIC_TIPS.ancillaryRevenueWindows}
                              value={money(detail.ancillary.revenue.four_wk_avg)}
                            />
                            <Kv
                              label="vs 4-wk avg"
                              tone={(detail.ancillary.revenue.lw_vs_4wk_pct ?? 0) <= -0.5 ? 'red' : undefined}
                              value={detail.ancillary.revenue.lw_vs_4wk_pct == null
                                ? '—'
                                : `${formatPercent(detail.ancillary.revenue.lw_vs_4wk_pct)}${
                                  detail.ancillary.revenue.lw != null && detail.ancillary.revenue.four_wk_avg != null
                                    ? ` (${(detail.ancillary.revenue.lw - detail.ancillary.revenue.four_wk_avg) >= 0 ? '+' : ''}${money(detail.ancillary.revenue.lw - detail.ancillary.revenue.four_wk_avg)})`
                                    : ''}`}
                            />
                            <Kv
                              label="Prior 8-wk avg / wk"
                              tip={METRIC_TIPS.ancillaryRevenueWindows}
                              value={money(detail.ancillary.revenue.prev_8wk_avg)}
                            />
                            <Kv label="13-wk total" value={money(detail.ancillary.revenue.total_13wk)} />
                            <Kv
                              label="Active weeks"
                              tip={METRIC_TIPS.ancillaryRevenueSpread}
                              value={detail.ancillary.revenue.active_weeks_13 == null
                                ? '—'
                                : `${detail.ancillary.revenue.active_weeks_13} of 13`}
                            />
                            <Kv
                              label="Services billed"
                              tip={METRIC_TIPS.ancillaryRevenueSpread}
                              value={detail.ancillary.revenue.product_count == null
                                ? '—'
                                : `${detail.ancillary.revenue.product_count} of 18`}
                            />
                            <Kv
                              label="Last billed"
                              value={`${formatContactDate(detail.ancillary.revenue.last_date)} · ${formatDaysAgo(detail.ancillary.revenue.days_since_last)}`}
                            />
                          </KvGrid>
                          {detail.ancillary.revenue.top_products && (
                            <p className="sv-popout-card-note">
                              Biggest services: {detail.ancillary.revenue.top_products}.
                            </p>
                          )}
                          <p className="sv-popout-card-note">
                            Net of credits and refunds, so a refunded week can read negative. The
                            4-week average excludes last week, so last week is measured against a
                            baseline it is not part of — which is why it will not tie exactly to
                            Plan My Day’s figure.
                          </p>
                        </>
                      )}
                    </>
                  )}
                </Card>

                <Card
                  title="What they buy"
                  note={`Sourcing openers. The profile block is a rolling ${detail.buy_profile.window_days ?? 90} days including Non-Manheim; the pricing read below is the rolling 3-month PERIOD, Manheim only.`}
                >
                  <KvGrid>
                    <Kv label="Purchases" value={formatCount(detail.buy_profile.total_buys)} tip={METRIC_TIPS.buyProfileWindow} />
                    <Kv label="Total spend" value={detail.buy_profile.total_spend == null ? '—' : `$${Math.round(detail.buy_profile.total_spend).toLocaleString()}`} tip={METRIC_TIPS.buyProfileWindow} />
                    <Kv
                      label="Avg paid vs base MMR"
                      tip={METRIC_TIPS.buyProfileVsMmr}
                      tone={isMmrRed(detail.buy_profile.avg_sold_vs_base_mmr) ? 'red' : undefined}
                      value={detail.buy_profile.avg_sold_vs_base_mmr == null
                        ? '—'
                        : `${detail.buy_profile.avg_sold_vs_base_mmr < 0 ? '−' : '+'}$${Math.abs(Math.round(detail.buy_profile.avg_sold_vs_base_mmr)).toLocaleString()}`}
                    />
                    <Kv label="Bids per purchase" value={detail.buy_profile.bids_per_purchase?.toFixed(1) ?? '—'} tip={METRIC_TIPS.buyProfileBids} />
                    <Kv label="Digital share" value={formatPercent(detail.buy_profile.pct_digital)} tip={METRIC_TIPS.buyProfileDigital} />
                    <Kv label="Last purchase" value={formatContactDate(detail.buy_profile.last_purchase_date)} />
                  </KvGrid>
                  <Kv label="Top makes" value={detail.buy_profile.top_makes || '—'} />
                  <Kv label="Top models" value={detail.buy_profile.top_models || '—'} />

                  {detail.buy_profile.market?.length ? (
                    <>
                      <div className="sv-popout-subhead">Supply and demand for what they buy</div>
                      <table className="sv-popout-table sv-buy-market">
                        <thead>
                          <tr>
                            <th>Make / model</th>
                            <th className="sv-num">Bought</th>
                            <th title="Wholesale days supply: inventory over the trailing 90-day sales pace. Lower is scarcer.">Wholesale</th>
                            <th title="Retail days supply: inventory over the trailing 90-day pace. Lower is scarcer.">Retail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.buy_profile.market.map((m) => (
                            <tr key={`${m.make}-${m.model}`}>
                              <td title={m.geo === 'national' ? 'No row for their market; national figures shown' : undefined}>
                                {m.make} {m.model}{m.geo === 'national' ? ' *' : ''}
                              </td>
                              <td className="sv-num">{formatCount(m.units)}</td>
                              <td>{marketCell(m.ws_days_supply, m.ws_demand)}</td>
                              <td>{marketCell(m.rt_days_supply, m.rt_demand)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="sv-popout-card-note">
                        Their top makes and models (rolling 90 days) against the market in their DMA right now.
                        Fewer days of supply and a hotter demand level mean the car is harder to source — the
                        opening for buying help. * = national figures, no row for their market.
                      </p>
                    </>
                  ) : null}

                  <div className="sv-popout-subhead">Buy pricing this period (Manheim)</div>
                  <KvGrid>
                    <Kv label="Avg price paid" value={detail.buy_profile.period_avg_purchase_price == null ? '—' : `$${Math.round(detail.buy_profile.period_avg_purchase_price).toLocaleString()}`} tip={METRIC_TIPS.buyPeriodPricing} />
                    <Kv label="Avg MMR bought" value={detail.buy_profile.period_avg_sold_mmr == null ? '—' : `$${Math.round(detail.buy_profile.period_avg_sold_mmr).toLocaleString()}`} tip={METRIC_TIPS.buyPeriodPricing} />
                    <Kv
                      label="Paid as % of MMR"
                      tip={METRIC_TIPS.buyToMmr}
                      tone={isMmrRed(detail.buy_profile.period_buy_to_mmr) ? 'red' : undefined}
                      value={formatPercent(detail.buy_profile.period_buy_to_mmr)}
                    />
                  </KvGrid>
                  <p className="sv-popout-card-note">
                    “Avg paid vs base MMR” is DOLLARS over the rolling-90 population; “Paid as % of MMR”
                    is the PERIOD ratio. Different populations — they will not agree, and that is expected.
                  </p>
                </Card>

                <Card
                  title="Cox Automotive performance"
                  wide
                  note={detail.cox_performance.has_data
                    ? `${formatMonthLabel(detail.cox_performance.month)} — ${detail.cox_performance.elapsed_days ?? '?'} of ${detail.cox_performance.month_days ?? '?'} days, data through ${formatContactDate(detail.cox_performance.as_of_date)}. Compared against ${formatMonthLabel(detail.cox_performance.month_prior)}. This block is MONTHLY; everything else in the drawer is daily.`
                    : undefined}
                >
                  {!detail.cox_performance.has_data ? (
                    <p className="sv-popout-card-note">
                      No Cox Automotive performance record for this rooftop. Unknown, not zero.
                    </p>
                  ) : (
                    <>
                      <div className="sv-popout-subhead">vAuto — inventory</div>
                      <KvGrid>
                        <LevelKv
                          label="Aged 45d+"
                          value={detail.cox_performance.aged_inventory_45d}
                          prior={detail.cox_performance.aged_inventory_45d_prior}
                          tip={METRIC_TIPS.coxAgedInventory}
                        />
                        <LevelKv
                          label="% aged"
                          value={detail.cox_performance.pct_aged_inventory}
                          prior={detail.cox_performance.pct_aged_inventory_prior}
                          percent
                          tip={METRIC_TIPS.coxPctAged}
                        />
                        <LevelKv
                          label="Used units"
                          value={detail.cox_performance.used_inventory}
                          prior={detail.cox_performance.used_inventory_prior}
                        />
                      </KvGrid>

                      <div className="sv-popout-subhead">Autotrader — advertising</div>
                      <KvGrid>
                        <LevelKv
                          label="Aged 45d+"
                          value={detail.cox_performance.atc_aged_listings_45d}
                          prior={detail.cox_performance.atc_aged_listings_45d_prior}
                          tip={METRIC_TIPS.coxAgedAdvertising}
                        />
                        <LevelKv
                          label="% aged"
                          value={detail.cox_performance.pct_aged_advertising}
                          prior={detail.cox_performance.pct_aged_advertising_prior}
                          percent
                          tip={METRIC_TIPS.coxPctAgedAdvertising}
                        />
                        <LevelKv
                          label="Used VINs advertised"
                          value={detail.cox_performance.atc_used_vins}
                          prior={detail.cox_performance.atc_used_vins_prior}
                        />
                      </KvGrid>

                      <div className="sv-popout-subhead">NextGear Capital — floor plan</div>
                      <KvGrid>
                        <LevelKv
                          label="Vehicles floored"
                          value={detail.cox_performance.ngc_vehicles_floored}
                          prior={detail.cox_performance.ngc_vehicles_floored_prior}
                        />
                        <PaceKv
                          label="Manheim buys floored"
                          mtd={detail.cox_performance.ngc_floored_buys_mtd}
                          prior={detail.cox_performance.ngc_floored_buys_prior}
                          pace={detail.cox_performance.ngc_floored_buys_pace}
                          reliable={detail.cox_performance.pace_reliable}
                        />
                        <Kv
                          label={`Avg floored price (${formatMonthLabel(detail.cox_performance.month_prior)})`}
                          tip={METRIC_TIPS.coxFlooredAvgPrice}
                          value={detail.cox_performance.ngc_floored_avg_price_prior == null
                            ? '—'
                            : `$${Math.round(detail.cox_performance.ngc_floored_avg_price_prior).toLocaleString()}`}
                        />
                        <Kv label="Finance program" value={detail.cox_performance.ngc_finance_program || '—'} />
                      </KvGrid>

                      <div className="sv-popout-subhead">Dealertrack DMS</div>
                      <KvGrid>
                        <PaceKv
                          label="Used wholesaled"
                          mtd={detail.cox_performance.dms_wholesaled_used_mtd}
                          prior={detail.cox_performance.dms_wholesaled_used_prior}
                          pace={detail.cox_performance.dms_wholesaled_used_pace}
                          reliable={detail.cox_performance.pace_reliable}
                        />
                      </KvGrid>

                      <div className="sv-popout-subhead">
                        Kelley Blue Book Instant Cash Offer — {formatMonthLabel(detail.cox_performance.month_prior)}
                      </div>
                      <KvGrid>
                        <Kv label="Offers made" value={formatCount(detail.cox_performance.ico_offers_total_prior)} tip={METRIC_TIPS.coxIcoPriorOnly} />
                        <Kv label="Offers accepted" value={formatCount(detail.cox_performance.ico_offers_accepted_prior)} tip={METRIC_TIPS.coxIcoPriorOnly} />
                      </KvGrid>

                      <div className="sv-popout-subhead">Rooftop</div>
                      <KvGrid>
                        <LevelKv
                          label="Retail used inventory"
                          value={detail.cox_performance.retail_used_inventory}
                          prior={detail.cox_performance.retail_used_inventory_prior}
                          tip={METRIC_TIPS.coxRooftopUnattributed}
                        />
                        <LevelKv
                          label="Wholesale used inventory"
                          value={detail.cox_performance.wholesale_used_inventory}
                          prior={detail.cox_performance.wholesale_used_inventory_prior}
                          tip={METRIC_TIPS.coxRooftopUnattributed}
                        />
                        <PaceKv
                          label="Trade-ins"
                          mtd={detail.cox_performance.trade_in_units_mtd}
                          prior={detail.cox_performance.trade_in_units_prior}
                          pace={detail.cox_performance.trade_in_units_pace}
                          reliable={detail.cox_performance.pace_reliable}
                          tip={METRIC_TIPS.coxTradeIns}
                        />
                        <Kv
                          label="Predicted used sales"
                          tip={METRIC_TIPS.coxPredicted}
                          value={formatCount(detail.cox_performance.predicted_used_car_sales == null
                            ? null
                            : Math.round(detail.cox_performance.predicted_used_car_sales))}
                        />
                        <Kv label="Type" value={detail.cox_performance.franchise_type || '—'} />
                        <Kv label="OEMs" value={detail.cox_performance.oem_list || '—'} />
                      </KvGrid>
                      <p className="sv-popout-card-note">
                        {detail.cox_performance.pace_reliable
                          ? 'Month-to-date figures are shown against the whole of last month, so the raw numbers are not like for like — the pace tag projects the partial month forward first.'
                          : 'Too early in the month to project a pace — month-to-date is shown against the whole of last month, so it will read low.'}
                        {' '}Retail/wholesale inventory and trade-ins are rooftop figures: they match no single
                        product feed, so they are deliberately not filed under one.
                      </p>

                      <div className="sv-popout-subhead" title={METRIC_TIPS.coxProductChips}>Products on record</div>
                      <div className="sv-anc-chips">
                        {coxProductMatrix(detail.cox_performance).map((p) => (
                          <span
                            key={p.label}
                            className={`sv-anc-chip${p.owned ? ' sv-anc-chip-on' : ''}`}
                            title={p.owned ? 'On record' : 'Not reported — whitespace, not a verified no'}
                          >
                            {p.label}
                          </span>
                        ))}
                      </div>
                      <p className="sv-popout-card-note">
                        Predicted used sales is Cox's forecast for the rooftop — a different number from the
                        dealer-reported “Monthly wholesale” above. Faded chips are not reported: the source
                        has no “no”, so none of these is a verified negative.
                      </p>
                    </>
                  )}
                </Card>
              </div>
            )}

            {section === 'activity' && (
              <div className="sv-popout-grid">
                <Card
                  title="Pipeline"
                  wide
                  note="Salesforce opportunities. Unlike everything else in the drawer this is about the rep's own commitments, not the dealer's behaviour — each line is something that can be worked today."
                >
                  {!detail.pipeline.has_data ? (
                    <p className="sv-popout-card-note">
                      No Salesforce opportunities on this client. An empty pipeline, which is
                      not the same as a healthy one.
                    </p>
                  ) : (
                    <>
                      <KvGrid>
                        <Kv
                          label="Open / total"
                          value={`${formatCount(detail.pipeline.opp_open)} / ${formatCount(detail.pipeline.opp_total)}`}
                        />
                        <Kv
                          label="Off track"
                          tip={METRIC_TIPS.pipelineOffTrackOpps}
                          tone={(detail.pipeline.offtrack_worst_score ?? 0) >= 100 ? 'red' : undefined}
                          value={formatCount(detail.pipeline.offtrack_opps)}
                        />
                        <Kv
                          label="Last deal activity"
                          tip={METRIC_TIPS.pipelineOppActivity}
                          value={detail.pipeline.inactivity_bucket
                            ? `${formatContactDate(detail.pipeline.last_open_opp_activity_date)} · ${detail.pipeline.inactivity_bucket}`
                            : formatContactDate(detail.pipeline.last_open_opp_activity_date)}
                        />
                        <Kv label="Next close" value={formatContactDate(detail.pipeline.next_close_date)} />
                        <Kv
                          label="Open expected lift"
                          tip={METRIC_TIPS.pipelineExpectedLift}
                          value={formatCount(detail.pipeline.open_expected_lift == null
                            ? null
                            : Math.round(detail.pipeline.open_expected_lift))}
                        />
                      </KvGrid>

                      {(detail.pipeline.offtrack_opps ?? 0) > 0 && (
                        <>
                          <div
                            className="sv-popout-subhead"
                            title={METRIC_TIPS.pipelineWorstScore}
                          >
                            Why it is off track
                          </div>
                          <KvGrid>
                            <Kv label="Pending Won past due" tip={METRIC_TIPS.pipelinePendingWon}
                              value={formatCount(detail.pipeline.pending_won_past_due)} />
                            <Kv label="Stalled 60d+" tip={METRIC_TIPS.pipelineStale}
                              value={formatCount(detail.pipeline.stage_stale_60d)} />
                            <Kv label="Pushed 3+ times" tip={METRIC_TIPS.pipelinePushed}
                              value={formatCount(detail.pipeline.close_date_pushed)} />
                            <Kv label="Past close date" tip={METRIC_TIPS.pipelinePastClose}
                              value={formatCount(detail.pipeline.past_close_date)} />
                          </KvGrid>
                        </>
                      )}

                      {(detail.pipeline.delivery_risk_opps ?? 0) > 0 && (
                        <>
                          <div className="sv-popout-subhead">Won but not delivering</div>
                          <KvGrid>
                            <Kv label="Deals at risk" tip={METRIC_TIPS.pipelineDeliveryRisk} tone="red"
                              value={formatCount(detail.pipeline.delivery_risk_opps)} />
                            <Kv label="Worst vs target" tip={METRIC_TIPS.pipelineDeliveryRiskPct}
                              value={detail.pipeline.delivery_risk_worst_pct_to_target == null
                                ? '—'
                                : `${Math.round(detail.pipeline.delivery_risk_worst_pct_to_target)}%`} />
                            <Kv label="Lift at risk" tip={METRIC_TIPS.pipelineExpectedLift}
                              value={formatCount(detail.pipeline.delivery_risk_expected_lift == null
                                ? null
                                : Math.round(detail.pipeline.delivery_risk_expected_lift))} />
                          </KvGrid>
                        </>
                      )}
                    </>
                  )}
                </Card>

                <Card
                  title="Contact activity (Salesforce)"
                  wide
                >
                  <KvGrid>
                    <Kv
                      label="Last CSE contact"
                      tip={METRIC_TIPS.engagementCseOnly}
                      value={`${formatContactDate(detail.engagement.last_cse_contact_date)} · ${formatDaysAgo(detail.engagement.days_since_last_cse_contact)}`}
                    />
                    <Kv
                      label="Last CSE live contact"
                      tip={METRIC_TIPS.engagementCseLive}
                      value={detail.engagement.last_cse_live_contact_date
                        ? `${formatContactDate(detail.engagement.last_cse_live_contact_date)} · ${formatDaysAgo(detail.engagement.days_since_last_cse_live_contact)}`
                        : 'Never'}
                    />
                    <Kv
                      label="Last CSE visit"
                      tip={METRIC_TIPS.engagementCseVisit}
                      value={detail.engagement.last_cse_in_person_contact_date
                        ? `${formatContactDate(detail.engagement.last_cse_in_person_contact_date)} · ${formatDaysAgo(detail.engagement.days_since_last_cse_in_person_contact)}`
                        : 'Never'}
                    />
                    <Kv label="Band" value={detail.engagement.engagement_band || '—'} />
                    <Kv
                      label="CSE 30d / 90d / all"
                      tip={METRIC_TIPS.engagementCseOnly}
                      value={`${detail.engagement.cse_contact_count_30d} / ${detail.engagement.cse_contact_count_90d} / ${detail.engagement.cse_contact_count_total}`}
                    />
                    <Kv
                      label="By role (all time)"
                      tip={METRIC_TIPS.engagementByRole}
                      value={`CSE ${detail.engagement.cse_contact_count_total} · RDS ${detail.engagement.rds_contact_count_total} · ManEx ${detail.engagement.manex_contact_count_total}`}
                    />
                    <Kv label="In person (90d)" value={detail.engagement.in_person_contact_count_90d} tip={METRIC_TIPS.engagementInPerson} />
                    <Kv label="Last channel" value={detail.engagement.last_contact_channel || 'Unclassified'} tip={METRIC_TIPS.engagementLiveFloor} />
                  </KvGrid>
                </Card>

                <Card title="CSE notes" note="The last 10 CSE tasks in Salesforce, newest first, with the note text.">
                  <NotesList notes={detail.cse_notes ?? []} empty="No CSE notes in the last 13 months." />
                </Card>

                <Card title="Other teams" note="The latest tasks logged by ManEx, RDS and other teams.">
                  <NotesList notes={otherTeamNotes} empty="Nothing from other teams recently." />
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pinned, not a card: closing out is the ACTION, and it should never need a
          scroll or the right tab to reach. */}
      {detail && !loading && (
        <div className="sv-popout-footer">
          <SalesStatusControl client={detail} onStatusChange={onStatusChange} variant="panel" />
          {detail.user_status_at && detail.user_status_by && (
            <span className="sv-popout-footer-note">
              {statusLabel(detail.user_status)} by {detail.user_status_by} ·{' '}
              {formatContactDate(detail.user_status_at)}
            </span>
          )}
          {detail.user_status_reason_text && (
            <span className="sv-popout-footer-reason">“{detail.user_status_reason_text}”</span>
          )}
        </div>
      )}
    </section>
  );
}

export default SalesClientDrawer;
